import { useEffect, useId, useState } from 'react';

import type { WorkspaceSettings } from '@noti/shared';

import type { WorkspaceSummary } from '../api';
import { updateWorkspace } from '../api';
import type { MessageKey } from '../i18n';
import { t } from '../i18n';
import { Glyph, ICON } from './icons';
import { McpTab, TokensTab, WebhooksTab } from './IntegrationTabs';

type SettingsTab = 'general' | 'connections' | 'tokens' | 'mcp' | 'webhooks' | 'import';

const TABS: {
  id: SettingsTab;
  label: MessageKey;
  sub: MessageKey;
  icon: string | readonly string[];
}[] = [
  {
    id: 'general',
    label: 'settings.tab.general',
    sub: 'settings.sub.general',
    icon: ICON.settings,
  },
  {
    id: 'connections',
    label: 'settings.tab.connections',
    sub: 'settings.sub.connections',
    icon: ICON.plug,
  },
  { id: 'tokens', label: 'settings.tab.tokens', sub: 'settings.sub.tokens', icon: ICON.key },
  { id: 'mcp', label: 'settings.tab.mcp', sub: 'settings.sub.mcp', icon: ICON.terminal },
  {
    id: 'webhooks',
    label: 'settings.tab.webhooks',
    sub: 'settings.sub.webhooks',
    icon: ICON.bolt,
  },
  { id: 'import', label: 'settings.tab.import', sub: 'settings.sub.import', icon: ICON.download },
];

const SWITCHES: {
  key: keyof WorkspaceSettings;
  label: MessageKey;
  sub: MessageKey;
}[] = [
  { key: 'allowGuests', label: 'settings.allowGuests', sub: 'settings.allowGuestsSub' },
  { key: 'membersInvite', label: 'settings.membersInvite', sub: 'settings.membersInviteSub' },
  { key: 'publish', label: 'settings.publish', sub: 'settings.publishSub' },
  { key: 'inheritAccess', label: 'settings.inheritAccess', sub: 'settings.inheritAccessSub' },
];

interface SettingsDialogProps {
  workspace: WorkspaceSummary;
  /** Public address of the workspace, shown and copied as-is. */
  address: string;
  onClose: () => void;
  /** Called with the saved workspace name so the sidebar can follow. */
  onSaved: (name: string) => void;
}

/**
 * Settings. General is the only tab that does anything: the other five are the
 * M5 surfaces (connections, access tokens, MCP, webhooks, import) and say so
 * instead of pretending to work.
 */
export function SettingsDialog({
  workspace,
  address,
  onClose,
  onSaved,
  onError,
}: SettingsDialogProps & { onError: () => void }): React.ReactElement {
  const titleId = useId();
  const [tab, setTab] = useState<SettingsTab>('general');
  const [name, setName] = useState(workspace.name);
  const [settings, setSettings] = useState<WorkspaceSettings>(workspace.settings);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(workspace.name);
    setSettings(workspace.settings);
  }, [workspace]);

  const save = (patch: { name?: string; settings?: Partial<WorkspaceSettings> }): void => {
    setError(null);
    void updateWorkspace(workspace.id, patch)
      .then((saved) => {
        setSettings(saved.settings);
        onSaved(saved.name);
      })
      .catch(() => setError(t('error.generic')));
  };

  const active = TABS.find((entry) => entry.id === tab);
  // The workspace address is `<origin>/<slug>`; the server lives at the origin.
  const baseUrl = address.replace(/\/[^/]*$/, '');

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="setdlg"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <nav className="setdlg__nav" aria-label={t('settings.sections')}>
          <div className="setdlg__navtitle">{t('settings.title')}</div>
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-current={entry.id === tab}
              className={entry.id === tab ? 'row nb is-active' : 'row nb'}
              onClick={() => setTab(entry.id)}
            >
              <Glyph d={entry.icon} />
              {t(entry.label)}
            </button>
          ))}
        </nav>
        <div className="setdlg__body">
          <div className="setdlg__head">
            <div className="setdlg__hgroup">
              <h2 className="setdlg__title" id={titleId}>
                {active ? t(active.label) : ''}
              </h2>
              <p className="setdlg__sub">{active ? t(active.sub) : ''}</p>
            </div>
            <button
              type="button"
              className="nb row setdlg__close"
              aria-label={t('settings.close')}
              onClick={onClose}
            >
              <Glyph d={ICON.close} />
            </button>
          </div>
          {tab === 'tokens' ? (
            <TokensTab workspace={workspace} baseUrl={address} onError={onError} kind="token" />
          ) : tab === 'connections' ? (
            <TokensTab
              workspace={workspace}
              baseUrl={address}
              onError={onError}
              kind="connection"
            />
          ) : tab === 'webhooks' ? (
            <WebhooksTab workspace={workspace} onError={onError} />
          ) : tab === 'mcp' ? (
            <McpTab baseUrl={baseUrl} />
          ) : tab === 'general' ? (
            <div className="setdlg__scroll">
              <div className="setdlg__field">
                <label className="setdlg__label" htmlFor="settings-name">
                  {t('settings.name')}
                </label>
                <input
                  id="settings-name"
                  className="ni setdlg__input"
                  value={name}
                  maxLength={64}
                  onChange={(event) => setName(event.target.value)}
                  onBlur={() => {
                    const trimmed = name.trim();
                    if (trimmed && trimmed !== workspace.name) save({ name: trimmed });
                  }}
                />
              </div>
              <div className="setdlg__field">
                <span className="setdlg__label">{t('settings.address')}</span>
                <div className="setdlg__address">
                  <code>{address}</code>
                  <button
                    type="button"
                    className="nb row setdlg__copy"
                    aria-label={t('settings.copyAddress')}
                    onClick={() => void navigator.clipboard.writeText(address)}
                  >
                    <Glyph d={ICON.copy} />
                  </button>
                </div>
              </div>
              <div className="setdlg__group">
                {SWITCHES.map((entry) => (
                  <div className="setdlg__row" key={entry.key}>
                    <div className="setdlg__rowtext">
                      <div className="setdlg__rowlabel">{t(entry.label)}</div>
                      <div className="setdlg__rowsub">{t(entry.sub)}</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={settings[entry.key]}
                      aria-label={t(entry.label)}
                      className={settings[entry.key] ? 'switch nb is-on' : 'switch nb'}
                      onClick={() => {
                        const next = { ...settings, [entry.key]: !settings[entry.key] };
                        setSettings(next);
                        save({ settings: { [entry.key]: next[entry.key] } });
                      }}
                    >
                      <span />
                    </button>
                  </div>
                ))}
              </div>
              {error ? (
                <p className="setdlg__error" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="setdlg__scroll">
              <p className="setdlg__empty">{t('settings.empty')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
